import React from 'react'
import {
  styled,
  Button as MuiButton,
  Typography,
  Dialog,
  Paper,
  useTheme,
  useMediaQuery,
  DialogProps,
  Fade,
  DialogTitle as MuiDialogTitle,
  DialogContent as MuiDialogContent,
  DialogActions,
  PaperProps,
} from '@mui/material'
import MaterialSymbol from './MaterialSymbol'
import { shouldForwardProp } from '../util/helpers'
import { colors } from '../themes/colors'

const DialogContent = styled(MuiDialogContent, {
  shouldForwardProp: (prop) => shouldForwardProp(['scrollable'], prop),
})<{ scrollable?: boolean }>`
  padding: 0px 10px;
  overflow-x: visible;
  width: 100%;
  overflow-y: ${({ scrollable }) => (scrollable ? 'scroll' : 'hidden')};
`
const StyledPaper = styled(Paper)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'white',
  padding: '24px 32px',
  gap: '24px',
  borderRadius: '12px',
  width: '100%',
  overflowX: 'hidden',

  [theme.breakpoints.down('sm')]: {
    padding: '18px 18px',
  },
}))

const DialogTitle = styled(MuiDialogTitle)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    fontSize: 'calc(min(max(17px, 6vw), 24px))',
  },
}))

const HeaderContaier = styled('div')<{ fullscreen?: boolean }>(({ fullscreen }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  marginTop: fullscreen ? 'var(--safe-area-statusbarHeight)' : '',
}))

export const CloseButton = styled(MuiButton)(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.colors.grey700,
  padding: '4px 0 0 0',
  border: 'none',
  lineHeight: '150%',
  textDecoration: 'none',
  textUnderlineOffset: '2px',
  float: 'right',

  '&:hover': {
    border: 0,
    background: 'inherit',
    color: theme.colors.primary500,
    '& .MuiTypography-root': {
      color: 'inherit',
      textDecoration: 'underline',
    },
  },
  '&:active': {
    color: theme.colors.primary900,
  },
  '& .MuiTypography-root': {
    color: 'inherit',
  },
}))

interface ModalProps extends DialogProps {
  handleClose: () => void
  testId?: string
  header?: React.ReactNode
  footer?: React.ReactNode
  scrollRef?: React.Ref<unknown>
  paperProps?: Partial<PaperProps>
  scrollable?: boolean
}

export const Modal = ({
  open,
  title,
  children,
  handleClose,
  testId,
  header,
  footer,
  scrollRef,
  scrollable = true,
  paperProps,
  ...props
}: ModalProps) => {
  const theme = useTheme()
  const isSm = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <Dialog
      scroll='paper'
      fullScreen={isSm || props.fullScreen}
      PaperComponent={StyledPaper}
      PaperProps={paperProps}
      TransitionComponent={Fade}
      open={open}
      onClose={handleClose}
      aria-labelledby={`modal-${title}`}
      slotProps={{
        backdrop: {
          id: `${testId}--close-backdrop`,
          //eslint-disable-next-line
          //@ts-ignore
          'data-testid': `${testId}--close-backdrop`,
          timeout: 500,
        },
      }}
      data-testid={testId}
      {...props}
    >
      <HeaderContaier fullscreen={isSm || props.fullScreen}>
        <DialogTitle>
          {title}
          <CloseButton
            id={`${testId}--close-button`}
            data-testid={`${testId}--close`}
            variant='outlined'
            disableTouchRipple
            onClick={handleClose}
          >
            <Typography variant='body2'>Close</Typography>
            <MaterialSymbol>close</MaterialSymbol>
          </CloseButton>
        </DialogTitle>
        {header}
      </HeaderContaier>

      <DialogContent
        scrollable={scrollable}
        ref={scrollRef}
        sx={{ mb: -3, pb: 3, pl: 0, pr: 0, ml: 0 }}
      >
        {children}
      </DialogContent>
      <DialogActions
        sx={{
          borderTop: footer ? `1px solid ${colors.primary200}` : 'none',
          pt: footer ? '1.5rem' : 0,
        }}
      >
        {footer}
      </DialogActions>
    </Dialog>
  )
}

export default Modal
